import React from 'react';
import { Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { Experience, AgeBand } from '../../../../../context/dataExperienceContext';
import { getAgeBandLabel } from '../label-helpers';
import { useTravelers, TravelersState } from '../../../../../context/travelersContext';

interface TravelersConfig {
    label: string;
    ageRange: string;
    min: number;
    max: number;
}

interface TravelersSelectorProps {
    pricingInfo: Experience['product']['pricingInfo'];
    bookingRequirements: Experience['product']['bookingRequirements'];
}

const TravelersSelector: React.FC<TravelersSelectorProps> = ({ 
    pricingInfo, 
    bookingRequirements
}) => {
    const { travelers, setTravelers } = useTravelers();

    const travelersConfig = pricingInfo.ageBands.reduce((config, band) => {
        if (!band.ageBand) return config; 

        const minTravelers = band.minTravelersPerBooking || ((band.ageBand === 'ADULT' && bookingRequirements.requiresAdultForBooking) ? 1 : 0);

        return {
            ...config,
            [band.ageBand]: {
                label: getAgeBandLabel(band.ageBand),
                ageRange: band.startAge !== undefined && band.endAge !== undefined
                    ? `(Entre ${band.startAge} y ${band.endAge} años)`
                    : '(Cualquier edad)',
                min: minTravelers,
                max: band.maxTravelersPerBooking || bookingRequirements.maxTravelersPerBooking
            }
        };
    }, {} as Record<AgeBand, TravelersConfig>);

    const getTotalTravelers = (): number => {
        return Object.values(travelers).reduce((sum: number, count) => sum + (count || 0), 0);
    };

    const handleTravelerChange = (type: AgeBand, increment: boolean) => {
        const newTravelers: TravelersState = { ...travelers };
        const currentTotal = getTotalTravelers();

        if (increment) {
            if (currentTotal < bookingRequirements.maxTravelersPerBooking) {
                newTravelers[type] = (newTravelers[type] || 0) + 1;
            }
        } else {
            if ((newTravelers[type] || 0) > (travelersConfig[type]?.min || 0)) {
                newTravelers[type] = (newTravelers[type] || 0) - 1;
            }
        }

        // Ensure we still meet minimum requirements
        const newTotal = Object.values(newTravelers).reduce((sum: number, count) => sum + (count || 0), 0);
        if (newTotal >= bookingRequirements.minTravelersPerBooking) {
            setTravelers(newTravelers);
        }
    };

    // Sort the traveler types in the desired order: ADULT, CHILD, INFANT
    const sortedTravelerTypes = Object.entries(travelersConfig).sort(([typeA], [typeB]) => {
        const order = { ADULT: 0, CHILD: 1, INFANT: 2 };
        return (order[typeA as keyof typeof order] || 3) - (order[typeB as keyof typeof order] || 3);
    });

    return (
        <div className="travelers-selector" onClick={e => e.stopPropagation()}>
            <p>Puede seleccionar hasta {bookingRequirements.maxTravelersPerBooking} viajeros en total.</p>

            {sortedTravelerTypes.map(([type, config]) => (
                <div key={type} className="availability__traveler-type">
                    <h3>{config.label}</h3>
                    <p>{config.ageRange}</p>
                    <div className="traveler-controls">
                        <p>Mínimo: {config.min}, máximo: {config.max}</p>
                        <div className="controls">
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleTravelerChange(type as AgeBand, false);
                                }}
                                disabled={(travelers[type as AgeBand] || 0) <= config.min}
                                size="small"
                            >
                                <FontAwesomeIcon icon={faMinus} />
                            </Button>
                            <span>
                                {travelers[type as AgeBand] || 0}
                            </span>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleTravelerChange(type as AgeBand, true);
                                }}
                                disabled={getTotalTravelers() >= bookingRequirements.maxTravelersPerBooking}
                                size="small"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default TravelersSelector; 